import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAuth } from '../context/AuthContext';
import { 
  faUpload,
  faPlay,
  faClipboardCheck,
  faSearch,
  faFileUpload,
  faFileMedical,
  faSpinner,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import ChannelMappingModal from '../components/ChannelMappingModal';
import API_URL from '../config';

const Home = () => {
  const [isDragging, setIsDragging] = useState(false);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [channelModal, setChannelModal] = useState(null); // { recordingId, channels }
  const [launchingDemo, setLaunchingDemo] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    fetchRecentRecordings();
  }, []);

  const fetchRecentRecordings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/recording/recent/`);
      if (response.ok) {
        const data = await response.json();
        // Format the backend data for the frontend ActivityItem
        const formatted = data.map(rec => ({
          id: rec.id,
          type: 'analysis',
          filename: `Study for ${rec.patient_name || `Patient #${rec.patient}`}`,
          time: new Date(rec.last_opened).toLocaleString(),
          status: 'completed',
          description: `Recorded on ${rec.recording_date}`
        }));
        setRecentActivity(formatted);
      }
    } catch (e) {
      console.error("Failed to fetch recent recordings", e);
    } finally {
      setLoading(false);
    }
  };

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleFileUpload = async (file) => {
    setUploading(true);
    const formData = new FormData();
    formData.append('edf_file', file);
    try {
      const response = await fetch(`${API_URL}/api/recording/upload/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: formData
      });
      if (response.ok) {
        const data = await response.json();
        setUploading(false);
        setChannelModal({ recordingId: data.recording_id, channels: data.channels || [] });
      } else {
        alert('Failed to upload file');
        setUploading(false);
      }
    } catch (err) {
      console.error(err);
      alert('Error uploading file');
      setUploading(false);
    }
  };

  const handleLaunchDemo = async () => {
    setLaunchingDemo(true);
    try {
      const response = await fetch(`${API_URL}/api/recording/launch-demo/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        navigate(`/analysis/${data.recording_id}/report`);
      } else {
        const err = await response.json().catch(() => ({}));
        alert(err.error || 'Failed to launch demo. Please try again.');
      }
    } catch (e) {
      alert('Error launching demo. Please check your connection.');
    } finally {
      setLaunchingDemo(false);
    }
  };

  const handleChannelMappingConfirm = () => {
    if (channelModal) {
      navigate(`/analysis/${channelModal.recordingId}/report`);
    }
    setChannelModal(null);
  };

  const handleChannelMappingCancel = () => {
    setChannelModal(null);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    if (uploading) return;
    const files = Array.from(e.dataTransfer.files);
    const edfFiles = files.filter(file => file.name.toLowerCase().endsWith('.edf'));
    if (edfFiles.length > 0) {
      handleFileUpload(edfFiles[0]);
    } else {
      alert("Please drop a valid .edf file");
    }
  }, [uploading, navigate]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.name.toLowerCase().endsWith('.edf')) {
      handleFileUpload(file);
    }
  };

  const handleDeleteRecording = async (id) => {
    if (!window.confirm('Are you sure you want to delete this recording?')) return;
    try {
      const response = await fetch(`${API_URL}/api/recording/${id}/`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      if (response.ok) {
        setRecentActivity(recentActivity.filter(r => r.id !== id));
      } else {
        alert('Failed to delete recording');
      }
    } catch (err) {
      alert('Error deleting recording');
    }
  };

  const QuickAction = ({ icon, label, color = "blue", to }) => (
    <Link 
      to={to}
      className="flex items-center p-3 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
    >
      <div className={`w-8 h-8 rounded-lg bg-${color}-50 dark:bg-${color}-900/20 flex items-center justify-center mr-3`}>
        <FontAwesomeIcon icon={icon} className={`text-${color}-600 dark:text-${color}-400`} />
      </div>
      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{label}</span>
    </Link>
  );

  const ActivityItem = ({ activity }) => (
    <Link
      to={`/analysis/${activity.id}/report`}
      className="flex flex-col sm:flex-row sm:items-center p-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-lg transition-colors duration-200 border border-transparent hover:border-gray-200 dark:hover:border-gray-600 group"
      style={{ textDecoration: 'none' }}
    >
      <div className="flex items-center mb-2 sm:mb-0">
        <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center shrink-0">
          <FontAwesomeIcon icon={faFileMedical} className="text-blue-500 dark:text-blue-400" />
        </div>
      </div>
      <div className="sm:ml-4 flex-grow">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">{activity.filename}</p>
          <span className="text-xs text-gray-500 dark:text-gray-400 mt-1 sm:mt-0">{activity.time}</span>
        </div>
        <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">{activity.description}</p>
      </div>
      <button
        className="ml-4 p-2 bg-red-600 hover:bg-red-700 text-white rounded-full transition-colors duration-200 flex items-center justify-center"
        onClick={e => { e.preventDefault(); e.stopPropagation(); handleDeleteRecording(activity.id); }}
        title="Delete recording"
        style={{ height: '32px', width: '32px' }}
      >
        <FontAwesomeIcon icon={faTrash} />
      </button>
    </Link>
  );

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6">
      {channelModal && (
        <ChannelMappingModal
          recordingId={channelModal.recordingId}
          channels={channelModal.channels}
          onConfirm={handleChannelMappingConfirm}
          onCancel={handleChannelMappingCancel}
        />
      )}
      {/* Welcome Section */}
      <div className="mb-8 text-left">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
          Welcome to SleepScorer, {user?.first_name || 'Guest'}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 max-w-2xl">
          SleepScorer is an intelligent decision support system for sleep analysis. 
          It uses both Machine Learning models and the AASM scoring rules to provide accurate, standardized sleep reports.
          SleepScorer is intended for research and educational purposes only and is not yet intended for clinical use. <br />
          Get started by trying our demo or uploading your own EDF files.
        </p>
      </div>

      {/* Main Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-16 mb-8 relative">
        {/* Demo Section */}
        <div className="bg-gradient-to-r from-indigo-100 to-purple-100 dark:from-gray-800 dark:to-gray-700 rounded-xl p-6 text-gray-800 dark:text-gray-100 flex flex-col items-center justify-center border border-purple-200 dark:border-gray-600 shadow-sm transition-transform hover:scale-[1.02] duration-200">
          <div className="w-14 h-14 rounded-full bg-white/50 dark:bg-gray-800 flex items-center justify-center mb-4 shadow-sm">
            <FontAwesomeIcon icon={faPlay} className="text-purple-600 dark:text-purple-400 text-2xl" />
          </div>
          <h2 className="text-lg font-semibold mb-2">Try the Demo</h2>
          <p className="text-sm text-center mb-6 text-gray-600 dark:text-gray-300 max-w-xs">
            Experience the analysis platform with our pre-loaded sleep study data
          </p>
          <button
            onClick={handleLaunchDemo}
            disabled={launchingDemo}
            className={`px-6 py-2.5 ${launchingDemo ? 'bg-gray-400 cursor-not-allowed' : 'bg-purple-600 hover:bg-purple-700 cursor-pointer'} text-white font-medium text-sm rounded-lg transition-colors duration-200 shadow-sm`}
          >
            {launchingDemo ? (
              <><FontAwesomeIcon icon={faSpinner} spin className="mr-2" /> Loading...</>
            ) : 'Launch Demo'}
          </button>
        </div>

        {/* Or Separator - Visible on all screens */}
        <div className="hidden lg:block absolute left-1/2 top-1/2 -translate-y-1/2 -translate-x-1/2 z-10">
          <div className="bg-white dark:bg-gray-800 px-3 py-1 rounded-full border border-gray-200 dark:border-gray-700 shadow-sm">
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">or</span>
          </div>
        </div>

        {/* Mobile Or Separator */}
        <div className="lg:hidden flex items-center justify-center py-2">
          <div className="w-24 h-px bg-gray-200 dark:bg-gray-700"></div>
          <span className="mx-4 text-sm font-medium text-gray-400 dark:text-gray-500">or</span>
          <div className="w-24 h-px bg-gray-200 dark:bg-gray-700"></div>
        </div>

        {/* Upload Section */}
        <div 
          className={`relative border-2 border-dashed rounded-xl p-6 transition-all duration-200 flex flex-col items-center justify-center
            ${isDragging 
              ? 'border-blue-500 bg-blue-50 scale-[1.02] dark:border-blue-400 dark:bg-blue-900/20' 
              : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 bg-white dark:bg-gray-800 shadow-sm hover:shadow'}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-4 transition-colors ${
            isDragging ? 'bg-blue-100 dark:bg-blue-900/40' : 'bg-blue-50 dark:bg-blue-900/20'
          }`}>
            <FontAwesomeIcon icon={faFileUpload} className={`text-2xl ${
              isDragging ? 'text-blue-600 dark:text-blue-400' : 'text-blue-500 dark:text-blue-400'
            }`} />
          </div>
          <p className="text-base font-medium text-gray-900 dark:text-gray-100 mb-1">
            {isDragging ? 'Drop file to upload' : 'Drop your EDF file here'}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">or click to browse</p>
          <input 
            type="file" 
            accept=".edf" 
            className="hidden" 
            id="edf-upload-input"
            onChange={handleFileSelect}
            disabled={uploading}
          />
          <label 
            htmlFor="edf-upload-input"
            className={`px-6 py-2.5 ${uploading ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 cursor-pointer'} text-white font-medium text-sm rounded-lg transition-colors duration-200 shadow-sm`}
          >
            {uploading ? (
              <><FontAwesomeIcon icon={faSpinner} spin className="mr-2" /> Uploading...</>
            ) : "Select File"}
          </label>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-5 mb-8">
        <h2 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-4 px-1">Quick Actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <QuickAction 
            icon={faSearch} 
            label="Browse Records" 
            to="/analysis" 
            color="blue"
          />
          <QuickAction 
            icon={faClipboardCheck} 
            label="Recent Analyses" 
            to="/analysis" 
            color="green"
          />
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
        <div className="flex items-center justify-between p-5 border-b border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50">
          <h2 className="text-base font-medium text-gray-900 dark:text-gray-100">Recent Activity</h2>
          <Link to="/analysis" className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors">
            View all recordings &rarr;
          </Link>
        </div>
        
        <div className="p-3">
          {loading ? (
             <div className="flex justify-center items-center py-10">
               <FontAwesomeIcon icon={faSpinner} spin className="text-blue-500 text-2xl" />
             </div>
          ) : recentActivity.length === 0 ? (
             <div className="text-center py-10">
               <p className="text-gray-500 dark:text-gray-400 text-sm">No recent recordings found.</p>
             </div>
          ) : (
            <div className="space-y-1">
              {recentActivity.map(activity => (
                <ActivityItem key={activity.id} activity={activity} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Home;